import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Bell, User, FileText, Upload, Loader2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CITIES } from "@/data/categories";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Onaylandı", cls: "bg-green-50 text-green-700 border-green-200" },
  rejected: { label: "Reddedildi", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function SeekerDashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "profile");
  const [profile, setProfile] = useState({ title: "", location: "", phone: "", summary: "", skills: [], experience_years: "" });
  const [skillInput, setSkillInput] = useState("");
  const [notes, setNotes] = useState([]);
  const [apps, setApps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cvName, setCvName] = useState("");

  const loadApps = () => api.get("/my/applications").then((r) => setApps(r.data)).catch(() => {});

  useEffect(() => {
    if (user?.profile) {
      setProfile({
        title: user.profile.title || "", location: user.profile.location || "", phone: user.profile.phone || "",
        summary: user.profile.summary || "", skills: user.profile.skills || [], experience_years: user.profile.experience_years || "",
      });
      setCvName(user.profile.cv_filename || "");
    }
    api.get("/notifications").then((r) => setNotes(r.data.notifications)).catch(() => {});
    loadApps();
  }, [user]);

  const changeTab = (t) => { setTab(t); setParams({ tab: t }); };

  const save = async () => {
    const payload = { ...profile, experience_years: profile.experience_years ? parseInt(profile.experience_years) : null };
    await api.put("/profile", payload);
    await refreshUser();
    toast.success("Profil güncellendi");
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !profile.skills.includes(s)) { setProfile((p) => ({ ...p, skills: [...p.skills, s] })); setSkillInput(""); }
  };

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCvName(data.filename);
      await refreshUser();
      toast.success("CV yüklendi");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); } finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Panelim | isbul.org" description="İş arayan paneli" canonical="/panel" />
      <Header />
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1f222d]">Merhaba, {user?.name} 👋</h1>
            <p className="text-slate-500 mt-1">Profilini güncelle, başvurularını takip et.</p>
          </div>
          <Link to="/ai-eslestirme"><Button data-testid="dash-ai-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"><Sparkles size={16} className="mr-2" /> AI ile İş Bul</Button></Link>
        </div>

        <div className="flex gap-2 mt-6 border-b border-slate-200">
          {[["profile", "Profilim"], ["applications", `Başvurularım (${apps.length})`]].map(([k, label]) => (
            <button key={k} data-testid={`seeker-tab-${k}`} onClick={() => changeTab(k)} className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-[#1f222d]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "profile" && (
          <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5"><User size={20} className="text-blue-600" /><h2 className="font-bold text-lg text-[#1f222d]">Profil Bilgileri</h2></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Ünvan</label>
                  <input data-testid="seeker-title" value={profile.title} onChange={(e) => setProfile((p) => ({ ...p, title: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Pozisyon" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Şehir</label>
                  <select data-testid="seeker-location" value={profile.location} onChange={(e) => setProfile((p) => ({ ...p, location: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                    <option value="">Seçiniz</option>{CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Telefon *</label>
                  <input data-testid="seeker-phone" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="05xx xxx xx xx" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1f222d]">Deneyim (yıl)</label>
                  <input data-testid="seeker-exp" type="number" value={profile.experience_years} onChange={(e) => setProfile((p) => ({ ...p, experience_years: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="0" />
                </div>
              </div>

              <label className="text-sm font-medium text-[#1f222d] block mt-4">Yetenekler</label>
              <div className="flex gap-2 mt-1.5">
                <input data-testid="seeker-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Yetenek ekleyip Enter'a basın" />
                <Button data-testid="seeker-add-skill" onClick={addSkill} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">Ekle</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.skills.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-sm">{s}
                    <button onClick={() => setProfile((p) => ({ ...p, skills: p.skills.filter((x) => x !== s) }))}>×</button>
                  </span>
                ))}
              </div>

              <label className="text-sm font-medium text-[#1f222d] block mt-4">Özet</label>
              <textarea data-testid="seeker-summary" value={profile.summary} onChange={(e) => setProfile((p) => ({ ...p, summary: e.target.value }))} rows={3} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" placeholder="Kendinizi kısaca tanıtın" />

              <label className="text-sm font-medium text-[#1f222d] block mt-4">CV</label>
              <label data-testid="seeker-cv-upload" className="mt-1.5 flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-xl px-4 py-3 cursor-pointer hover:border-blue-400 transition-colors">
                {uploading ? <Loader2 size={18} className="animate-spin text-blue-600" /> : cvName ? <FileText size={18} className="text-blue-600" /> : <Upload size={18} className="text-slate-400" />}
                <span className="text-sm text-slate-600 truncate">{uploading ? "Yükleniyor..." : cvName || "CV dosyası yükle (PDF/DOCX)"}</span>
                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={uploadCv} />
              </label>

              <Button data-testid="seeker-save" onClick={save} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-5 font-semibold">Kaydet</Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 h-fit">
              <div className="flex items-center gap-2 mb-4"><Bell size={20} className="text-blue-600" /><h2 className="font-bold text-lg text-[#1f222d]">Bildirimler</h2></div>
              {notes.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">AI eşleştirme ve başvuru güncellemeleri burada belirir.</p>
              ) : (
                <div className="space-y-3">
                  {notes.slice(0, 8).map((n) => (
                    <Link key={n.id} to={n.job_slug ? `/ilan/${n.job_slug}` : "#"} className="block p-3 rounded-xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50/40 transition-colors">
                      <p className="text-sm font-semibold text-[#1f222d]">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "applications" && (
          <div className="mt-6">
            {apps.length === 0 ? (
              <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                <Briefcase size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-[#1f222d] font-semibold">Henüz başvurunuz yok</p>
                <p className="text-sm text-slate-500 mt-1">İlanları inceleyip başvurun, durumları burada takip edin.</p>
                <Link to="/ilanlar"><Button className="mt-5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">İş İlanlarına Göz At</Button></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {apps.map((a) => {
                  const st = STATUS[a.status] || STATUS.pending;
                  return (
                    <div key={a.id} data-testid={`seeker-app-${a.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <Link to={`/ilan/${a.job_slug}`} className="font-semibold text-[#1f222d] hover:text-blue-600">{a.job_title}</Link>
                        <p className="text-sm text-slate-500 mt-0.5">{a.company_name} · {new Date(a.created_at).toLocaleDateString("tr-TR")}</p>
                      </div>
                      <span className={`text-sm font-medium rounded-full px-3 py-1 border ${st.cls}`}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
